/**
 * Youidian Payment SDK - Client Module
 * 用于浏览器端集成，包含支付弹窗、状态轮询等功能
 * 不依赖 Node.js crypto 模块
 */

import { createSdkBrandLogo, createSdkFallbackArtwork } from "./brand-logo"
import {
	applyLocaleToUrl,
	getAutoResolvedLocale,
	HostedFrameModal,
} from "./hosted-modal"

/**
 * Payment event types from checkout pages
 */
export type PaymentEventType =
	| "PAYMENT_READY"
	| "PAYMENT_SUCCESS"
	| "PAYMENT_CANCELLED"
	| "PAYMENT_CLOSE"
	| "PAYMENT_RESIZE"
	| "PAYMENT_STARTED"

/**
 * Payment event data from postMessage
 */
export interface PaymentEventData {
	type: PaymentEventType
	orderId?: string
	height?: number
}

/**
 * Order status response
 */
export interface OrderStatus {
	orderId: string
	status: "PENDING" | "PAID" | "CANCELLED" | "REFUNDED" | "FAILED" | "EXPIRED"
	paidAt?: string
	channelTransactionId?: string
}

export interface PaymentUIThemeColors {
	primary?: string
	secondary?: string
	foreground?: string
	muted?: string
	surface?: string
}

/**
 * Payment UI Options
 */
export interface PaymentUIOptions {
	locale?: string
	onSuccess?: (orderId?: string) => void
	onCancel?: (orderId?: string) => void
	onClose?: () => void
	onFallbackBlocked?: (url: string) => void
	onFallbackOpen?: (url: string) => void
	onFallbackVisible?: () => void
	/** Origin to validate postMessage (defaults to '*', set for security) */
	allowedOrigin?: string
	/** Theme colors for SDK fallback/loading chrome. Defaults to Youidian green. */
	theme?: PaymentUIThemeColors
	/** Text for the fallback button shown when the checkout iframe is slow. */
	fallbackButtonText?: string
	/** Text for retrying the embedded checkout iframe. */
	fallbackRetryText?: string
	/** How long to wait for PAYMENT_READY or PAYMENT_RESIZE. Defaults to 8000ms. */
	iframeLoadTimeoutMs?: number
	/** Description shown while the hosted checkout iframe is loading. */
	loadingDescription?: string
	/** Title shown while the hosted checkout iframe is loading. */
	loadingTitle?: string
}

const DEFAULT_PAYMENT_IFRAME_LOAD_TIMEOUT_MS = 8000
const PAYMENT_UI_LOG_PREFIX = "[Youidian PaymentUI]"

type ResolvedPaymentTheme = {
	foreground: string
	muted: string
	primary: string
	primary10: string
	primary14: string
	primary20: string
	primary28: string
	primary36: string
	primary64: string
	secondary: string
	surface: string
}

function hexToRgb(value: string) {
	const normalized = value.trim().replace(/^#/, "")
	if (!/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(normalized)) return null
	const hex =
		normalized.length === 3
			? normalized
					.split("")
					.map((char) => `${char}${char}`)
					.join("")
			: normalized
	const number = Number.parseInt(hex, 16)
	return {
		b: number & 255,
		g: (number >> 8) & 255,
		r: (number >> 16) & 255,
	}
}

function alphaColor(value: string, alpha: number) {
	const rgb = hexToRgb(value)
	if (!rgb)
		return `color-mix(in srgb, ${value} ${Math.round(alpha * 100)}%, transparent)`
	return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`
}

function resolvePaymentTheme(options?: PaymentUIOptions): ResolvedPaymentTheme {
	const primary = options?.theme?.primary || "#16a34a"
	const secondary =
		options?.theme?.secondary || "color-mix(in srgb, #16a34a 78%, white)"
	const foreground = options?.theme?.foreground || "#0f172a"
	const muted = options?.theme?.muted || "#64748b"
	const surface = options?.theme?.surface || "#ffffff"

	return {
		foreground,
		muted,
		primary,
		primary10: alphaColor(primary, 0.1),
		primary14: alphaColor(primary, 0.14),
		primary20: alphaColor(primary, 0.2),
		primary28: alphaColor(primary, 0.28),
		primary36: alphaColor(primary, 0.36),
		primary64: alphaColor(primary, 0.64),
		secondary,
		surface,
	}
}

function logPaymentInfo(message: string, details?: Record<string, unknown>) {
	if (typeof console === "undefined") return
	console.info(PAYMENT_UI_LOG_PREFIX, message, details || {})
}

function logPaymentWarn(message: string, details?: Record<string, unknown>) {
	if (typeof console === "undefined") return
	console.warn(PAYMENT_UI_LOG_PREFIX, message, details || {})
}

function getPaymentIframeLoadTimeoutMs(options?: PaymentUIOptions) {
	const timeout = options?.iframeLoadTimeoutMs
	if (typeof timeout !== "number" || !Number.isFinite(timeout)) {
		return DEFAULT_PAYMENT_IFRAME_LOAD_TIMEOUT_MS
	}
	return Math.max(0, timeout)
}

function isChinesePaymentLocale(locale?: string) {
	return locale?.toLowerCase().startsWith("zh") || false
}

function getPaymentFallbackText(options?: PaymentUIOptions) {
	const isChinese = isChinesePaymentLocale(options?.locale)
	return {
		buttonText:
			options?.fallbackButtonText ||
			(isChinese ? "在新页面中打开" : "Open in new page"),
		description: isChinese
			? "当前网络可能较慢，建议重新尝试或在新页面中打开，以获得更流畅的支付体验。"
			: "Your network may be slow. Try again or open the payment page in a new page to continue.",
		retryText:
			options?.fallbackRetryText || (isChinese ? "重新尝试" : "Try again"),
		title: isChinese ? "支付页面打开较慢" : "Payment page is taking longer",
	}
}

function getPaymentBrandText(options?: PaymentUIOptions) {
	const isChinese = isChinesePaymentLocale(options?.locale)
	return {
		language: isChinese ? "zh" : "en",
		name: isChinese ? "优易点" : "Youidian",
		subtitle: isChinese ? "开发者服务平台" : "Developer Service Platform",
	}
}

function getPaymentBrandLoadingText(options?: PaymentUIOptions) {
	const isChinese = isChinesePaymentLocale(options?.locale)
	return {
		language: isChinese ? "zh" : "en",
		name: isChinese ? "优易点" : "Youidian",
		subtitle: isChinese ? "开发者服务中台" : "Developer Service",
	}
}

function getPaymentLoadingText(options?: PaymentUIOptions) {
	const isChinese = isChinesePaymentLocale(options?.locale)
	return (
		options?.loadingTitle ||
		options?.loadingDescription ||
		(isChinese ? "加载中..." : "Loading...")
	)
}

function applyPaymentPanelStyle(
	panel: HTMLDivElement,
	theme: ResolvedPaymentTheme,
) {
	Object.assign(panel.style, {
		position: "absolute",
		inset: "0",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: "28px",
		background: `linear-gradient(180deg, ${theme.surface} 0%, rgba(248,250,252,0.98) 100%)`,
		color: theme.foreground,
		padding: "28px 24px",
		textAlign: "center",
		zIndex: "1",
		boxShadow: "none",
		backdropFilter: "blur(16px)",
	})
}

function createPaymentIcon(type: "external" | "refresh") {
	const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
	svg.setAttribute("viewBox", "0 0 24 24")
	svg.setAttribute("fill", "none")
	svg.setAttribute("aria-hidden", "true")
	Object.assign(svg.style, {
		display: "block",
		flex: "0 0 auto",
	})

	const paths =
		type === "external"
			? [
					"M7 17L17 7",
					"M10 7H17V14",
					"M6.5 6.5H5.5A2.5 2.5 0 0 0 3 9V18.5A2.5 2.5 0 0 0 5.5 21H15A2.5 2.5 0 0 0 17.5 18.5V17.5",
				]
			: [
					"M20 6V11H15",
					"M4 18V13H9",
					"M18.2 9A7 7 0 0 0 6.2 6.6L4 9",
					"M5.8 15A7 7 0 0 0 17.8 17.4L20 15",
				]

	for (const value of paths) {
		const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
		path.setAttribute("d", value)
		path.setAttribute("stroke", "currentColor")
		path.setAttribute("stroke-width", "2")
		path.setAttribute("stroke-linecap", "round")
		path.setAttribute("stroke-linejoin", "round")
		svg.appendChild(path)
	}

	return svg
}

function createPaymentBrandLoading(
	options: PaymentUIOptions | undefined,
	theme: ResolvedPaymentTheme,
) {
	const brand = getPaymentBrandLoadingText(options)
	const wrapper = document.createElement("div")
	Object.assign(wrapper.style, {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		margin: "0 auto 16px",
	})

	wrapper.appendChild(
		createSdkBrandLogo({
			animated: true,
			brand,
			brandWidth: 292,
			copyWidth: 176,
			gap: 6,
			markMarginRight: -10,
			markSize: 112,
			nameSize: 39,
			subtitleMarginTop: 8,
			subtitleSize: 14,
			subtitleWeight: "700",
			theme,
			variant: "brand",
		}),
	)
	return wrapper
}

function createPaymentPlatformLogo(
	options: PaymentUIOptions | undefined,
	theme: ResolvedPaymentTheme,
) {
	const brand = getPaymentBrandText(options)
	const wrapper = document.createElement("div")
	Object.assign(wrapper.style, {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		margin: "0 auto 10px",
	})
	wrapper.appendChild(
		createSdkBrandLogo({
			brand,
			markSize: 48,
			nameSize: brand.language === "zh" ? 22 : 22,
			subtitleSize: brand.language === "zh" ? 9 : 9,
			theme,
		}),
	)
	return wrapper
}

function createPaymentPanelContent() {
	const content = document.createElement("div")
	Object.assign(content.style, {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		maxWidth: "364px",
		minHeight: "100%",
		width: "100%",
	})

	return content
}

function createPaymentPanelTitle(value: string, theme?: ResolvedPaymentTheme) {
	const title = document.createElement("div")
	title.textContent = value
	Object.assign(title.style, {
		color: theme?.foreground || "#0f172a",
		fontSize: "24px",
		fontWeight: "900",
		lineHeight: "1.22",
		marginBottom: "16px",
	})
	return title
}

function createPaymentPanelDescription(
	value: string,
	theme?: ResolvedPaymentTheme,
) {
	const description = document.createElement("div")
	description.textContent = value
	Object.assign(description.style, {
		color: theme?.muted || "#64748b",
		fontSize: "14px",
		fontWeight: "600",
		lineHeight: "1.62",
		margin: "0 auto",
		maxWidth: "320px",
	})
	return description
}

function createPaymentLoadingDescription(
	value: string,
	theme?: ResolvedPaymentTheme,
) {
	const description = document.createElement("div")
	description.textContent = value
	Object.assign(description.style, {
		color: theme?.muted || "#64748b",
		fontSize: "14px",
		fontWeight: "500",
		lineHeight: "1.25",
		margin: "12px auto 0",
		maxWidth: "320px",
	})
	return description
}

function createPaymentFallbackMessage(options: {
	description: string
	theme: ResolvedPaymentTheme
	title: string
}) {
	const message = document.createElement("div")
	Object.assign(message.style, {
		margin: "0 auto",
		textAlign: "center",
	})
	message.appendChild(createSdkFallbackArtwork(options.theme))
	message.appendChild(createPaymentPanelTitle(options.title, options.theme))
	message.appendChild(
		createPaymentPanelDescription(options.description, options.theme),
	)
	return message
}

function createPaymentLoadingPanel(options?: PaymentUIOptions) {
	const panel = document.createElement("div")
	const theme = resolvePaymentTheme(options)
	applyPaymentPanelStyle(panel, theme)

	const content = createPaymentPanelContent()
	content.appendChild(createPaymentBrandLoading(options, theme))
	content.appendChild(
		createPaymentLoadingDescription(getPaymentLoadingText(options), theme),
	)
	panel.appendChild(content)
	return panel
}

function createPaymentButton(
	value: string,
	options?: {
		icon?: "external" | "refresh"
		primary?: boolean
		theme?: ResolvedPaymentTheme
	},
) {
	const primary = options?.primary || false
	const theme = options?.theme || resolvePaymentTheme()
	const button = document.createElement("button")
	button.type = "button"
	Object.assign(button.style, {
		width: "100%",
		height: "50px",
		borderRadius: "14px",
		border: primary
			? `1px solid ${theme.primary}`
			: `1px solid ${theme.primary64}`,
		background: primary
			? `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`
			: "#ffffff",
		color: primary ? "#ffffff" : theme.primary,
		cursor: "pointer",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		gap: "10px",
		fontSize: "15px",
		fontWeight: "800",
		marginTop: primary ? "12px" : "0",
		boxShadow: primary ? `0 14px 28px ${theme.primary20}` : "none",
	})
	if (options?.icon) {
		const icon = createPaymentIcon(options.icon)
		Object.assign(icon.style, {
			width: "17px",
			height: "17px",
		})
		button.appendChild(icon)
	}
	const label = document.createElement("span")
	label.textContent = value
	button.appendChild(label)
	return button
}

function createPaymentFallbackPanel(options: {
	buttonText: string
	description: string
	paymentOptions?: PaymentUIOptions
	onOpen: () => void
	onRetry: () => void
	retryText: string
	title: string
}) {
	const panel = document.createElement("div")
	const theme = resolvePaymentTheme(options.paymentOptions)
	applyPaymentPanelStyle(panel, theme)

	const content = createPaymentPanelContent()
	Object.assign(content.style, {
		justifyContent: "space-between",
	})

	const main = document.createElement("div")
	Object.assign(main.style, {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		marginTop: "38px",
		width: "100%",
	})
	main.appendChild(
		createPaymentFallbackMessage({
			description: options.description,
			theme,
			title: options.title,
		}),
	)

	const actions = document.createElement("div")
	Object.assign(actions.style, {
		marginTop: "46px",
		width: "100%",
	})

	const retryButton = createPaymentButton(options.retryText, {
		icon: "refresh",
		theme,
	})
	retryButton.onclick = options.onRetry
	actions.appendChild(retryButton)

	const openButton = createPaymentButton(options.buttonText, {
		icon: "external",
		primary: true,
		theme,
	})
	openButton.onclick = options.onOpen
	actions.appendChild(openButton)

	main.appendChild(actions)
	content.appendChild(main)
	content.appendChild(createPaymentPlatformLogo(options.paymentOptions, theme))
	panel.appendChild(content)
	return panel
}

/**
 * Poll Options
 */
export interface PollOptions {
	/** Polling interval in ms (default: 3000) */
	interval?: number
	/** Timeout in ms (default: 300000 = 5 minutes) */
	timeout?: number
	/** Callback on each status check */
	onStatusChange?: (status: OrderStatus) => void
}

/**
 * Client-side Payment UI Helper
 * 浏览器端支付 UI 辅助类，用于弹出支付窗口和轮询订单状态
 */
/**
 * Params for opening payment directly without manual URL construction
 */
export interface PaymentParams {
	appId: string
	userId?: string
	/** Existing Youidian order ID - opens checkout for an already-created order */
	orderId?: string
	/** Product ID - use with priceId for direct ID-based payment */
	productId?: string
	/** Price ID - use with productId for direct ID-based payment */
	priceId?: string
	/** Product code - use with locale for code-based payment (alternative to productId/priceId) */
	productCode?: string
	/** Optional custom recharge amount in the smallest currency unit */
	customAmount?: {
		amount: number
		currency: string
	}

	/**
	 * @deprecated Use checkoutUrl instead
	 * Defaults to https://pay.imgto.link
	 */
	baseUrl?: string

	/**
	 * Checkout page URL (e.g. https://pay.youidian.com)
	 * Defaults to https://pay.imgto.link
	 */
	checkoutUrl?: string
}

/**
 * Client-side Payment UI Helper
 * 浏览器端支付 UI 辅助类，用于弹出支付窗口和轮询订单状态
 */
export class PaymentUI extends HostedFrameModal<PaymentEventData> {
	private activeCheckoutAppId: string | null = null
	private activeCheckoutBaseUrl: string | null = null
	private activeOrderId: string | null = null
	private paymentCompleted = false
	private cancelRequestedOrderId: string | null = null
	private iframeFallbackPanel: HTMLDivElement | null = null
	private iframeLoadingPanel: HTMLDivElement | null = null
	private iframeLoadTimer: number | null = null
	private iframeReady = false

	private cleanupPaymentFrameState() {
		if (typeof window !== "undefined" && this.iframeLoadTimer) {
			window.clearTimeout(this.iframeLoadTimer)
		}
		if (this.iframeFallbackPanel?.parentNode) {
			this.iframeFallbackPanel.parentNode.removeChild(this.iframeFallbackPanel)
		}
		if (this.iframeLoadingPanel?.parentNode) {
			this.iframeLoadingPanel.parentNode.removeChild(this.iframeLoadingPanel)
		}
		this.iframeFallbackPanel = null
		this.iframeLoadingPanel = null
		this.iframeLoadTimer = null
		this.iframeReady = false
	}

	private markPaymentIframeReady() {
		if (this.iframeReady) return
		this.iframeReady = true
		if (typeof window !== "undefined" && this.iframeLoadTimer) {
			window.clearTimeout(this.iframeLoadTimer)
			this.iframeLoadTimer = null
		}
		if (this.iframeFallbackPanel?.parentNode) {
			this.iframeFallbackPanel.parentNode.removeChild(this.iframeFallbackPanel)
		}
		if (this.iframeLoadingPanel?.parentNode) {
			this.iframeLoadingPanel.parentNode.removeChild(this.iframeLoadingPanel)
		}
		this.iframeFallbackPanel = null
		this.iframeLoadingPanel = null
	}

	private showPaymentIframeLoading(
		container: HTMLDivElement,
		options?: PaymentUIOptions,
	) {
		if (this.iframeLoadingPanel || this.iframeReady) return

		const panel = createPaymentLoadingPanel(options)
		this.iframeLoadingPanel = panel
		container.appendChild(panel)
	}

	private openFallbackPaymentPage(
		finalUrl: string,
		options?: PaymentUIOptions,
	) {
		if (typeof window === "undefined") return

		const popup = window.open(
			finalUrl,
			"youidian-payment-checkout",
			"popup=yes,width=480,height=760,resizable=yes,scrollbars=yes",
		)
		if (popup) {
			logPaymentInfo("Opened hosted checkout fallback page", {
				checkoutUrl: finalUrl,
			})
			options?.onFallbackOpen?.(finalUrl)
			try {
				popup.focus()
			} catch {
				// Ignore focus failures in restrictive browsers.
			}
			return
		}

		logPaymentWarn("Hosted checkout fallback popup was blocked", {
			checkoutUrl: finalUrl,
		})
		options?.onFallbackBlocked?.(finalUrl)
		window.location.assign(finalUrl)
	}

	private showPaymentIframeFallback(options: {
		container: HTMLDivElement
		finalUrl: string
		paymentOptions?: PaymentUIOptions
	}) {
		if (this.iframeReady || this.iframeFallbackPanel) return

		const { container, finalUrl, paymentOptions } = options
		logPaymentWarn("Hosted checkout iframe did not report ready in time", {
			checkoutUrl: finalUrl,
		})
		if (this.iframeLoadingPanel?.parentNode) {
			this.iframeLoadingPanel.parentNode.removeChild(this.iframeLoadingPanel)
		}
		this.iframeLoadingPanel = null

		const fallbackText = getPaymentFallbackText(paymentOptions)
		const panel = createPaymentFallbackPanel({
			buttonText: fallbackText.buttonText,
			description: fallbackText.description,
			onOpen: () => {
				this.openFallbackPaymentPage(finalUrl, paymentOptions)
			},
			onRetry: () => {
				this.iframeFallbackPanel = null
				if (panel.parentNode) {
					panel.parentNode.removeChild(panel)
				}
				this.iframeReady = false
				this.showPaymentIframeLoading(container, paymentOptions)
				if (this.iframe) {
					this.iframe.src = finalUrl
				}
				this.startPaymentIframeWatchdog({
					container,
					finalUrl,
					paymentOptions,
				})
			},
			paymentOptions,
			retryText: fallbackText.retryText,
			title: fallbackText.title,
		})

		this.iframeFallbackPanel = panel
		container.appendChild(panel)
		paymentOptions?.onFallbackVisible?.()
	}

	private startPaymentIframeWatchdog(options: {
		container: HTMLDivElement
		finalUrl: string
		paymentOptions?: PaymentUIOptions
	}) {
		if (typeof window === "undefined") return

		if (this.iframeLoadTimer) {
			window.clearTimeout(this.iframeLoadTimer)
		}
		const timeoutMs = getPaymentIframeLoadTimeoutMs(options.paymentOptions)
		if (timeoutMs === 0) {
			this.showPaymentIframeFallback(options)
			return
		}

		this.iframeLoadTimer = window.setTimeout(() => {
			this.iframeLoadTimer = null
			this.showPaymentIframeFallback(options)
		}, timeoutMs)
	}

	private cancelHostedOrder(reason = "user_cancel", orderId?: string) {
		const targetOrderId = orderId || this.activeOrderId
		if (
			!targetOrderId ||
			!this.activeCheckoutAppId ||
			!this.activeCheckoutBaseUrl ||
			this.paymentCompleted ||
			this.cancelRequestedOrderId === targetOrderId
		) {
			return
		}

		this.cancelRequestedOrderId = targetOrderId
		const url = `${this.activeCheckoutBaseUrl}/api/internal/checkout/cancel-order`
		const payload = JSON.stringify({
			appId: this.activeCheckoutAppId,
			orderId: targetOrderId,
			reason,
		})

		if (typeof navigator !== "undefined" && navigator.sendBeacon) {
			const sent = navigator.sendBeacon(
				url,
				new Blob([payload], { type: "application/json" }),
			)
			if (sent) return
		}

		void fetch(url, {
			method: "POST",
			body: payload,
			headers: { "Content-Type": "text/plain;charset=UTF-8" },
			keepalive: true,
			mode: "no-cors",
		}).catch(() => {
			this.cancelRequestedOrderId = null
		})
	}

	/**
	 * Opens the payment checkout page in an iframe modal.
	 * @param urlOrParams - The checkout page URL or payment parameters
	 * @param options - UI options
	 */
	openPayment(urlOrParams: string | PaymentParams, options?: PaymentUIOptions) {
		if (typeof document === "undefined") return // Server-side guard
		if (this.modal) return // Prevent multiple modals

		let checkoutUrl: string
		this.activeOrderId = null
		this.paymentCompleted = false
		this.cancelRequestedOrderId = null
		this.cleanupPaymentFrameState()
		if (typeof urlOrParams === "string") {
			checkoutUrl = urlOrParams
			try {
				const parsedUrl = new URL(checkoutUrl)
				this.activeCheckoutBaseUrl = parsedUrl.origin
				const parts = parsedUrl.pathname.split("/").filter(Boolean)
				const checkoutIndex = parts.indexOf("checkout")
				this.activeCheckoutAppId =
					checkoutIndex >= 0 ? parts[checkoutIndex + 1] || null : null
			} catch {
				this.activeCheckoutAppId = null
				this.activeCheckoutBaseUrl = null
			}
		} else {
			const {
				appId,
				orderId,
				productId,
				priceId,
				productCode,
				userId,
				customAmount,
				checkoutUrl: checkoutUrlParam,
				baseUrl = "https://pay.imgto.link",
			} = urlOrParams

			// 优先使用 checkoutUrl，其次 baseUrl，默认 https://pay.imgto.link
			const base = (checkoutUrlParam || baseUrl).replace(/\/$/, "")
			this.activeCheckoutAppId = appId
			this.activeCheckoutBaseUrl = base

			const query = new URLSearchParams()
			if (userId) query.set("userId", userId)
			if (customAmount) {
				const amount = Number(customAmount.amount)
				const currency = customAmount.currency.trim().toUpperCase()
				if (!Number.isInteger(amount) || amount <= 0) {
					throw new Error(
						"customAmount.amount must be a positive integer in the smallest currency unit",
					)
				}
				if (!/^[A-Z]{3}$/.test(currency)) {
					throw new Error(
						"customAmount.currency must be a 3-letter currency code",
					)
				}
				query.set("customAmount", String(amount))
				query.set("customCurrency", currency)
			}

			if (orderId) {
				const cleanOrderId = orderId.trim()
				if (!cleanOrderId) {
					throw new Error("orderId is required")
				}
				this.activeOrderId = cleanOrderId
				checkoutUrl = `${base}/checkout/${appId}/orders/${cleanOrderId}`
				const queryString = query.toString()
				if (queryString) checkoutUrl += `?${queryString}`
			} else if (productCode) {
				// Use product code route
				checkoutUrl = `${base}/checkout/${appId}/code/${productCode}?${query.toString()}`
			} else if (productId && priceId) {
				// Use ID-based route
				checkoutUrl = `${base}/checkout/${appId}/${productId}/${priceId}?${query.toString()}`
			} else {
				throw new Error(
					"Either orderId, productCode, or both productId and priceId are required",
				)
			}
		}

		const finalUrl = applyLocaleToUrl(
			checkoutUrl,
			getAutoResolvedLocale(options?.locale),
		)

		const hostedFrame = this.openHostedFrame(finalUrl, {
			allowedOrigin: options?.allowedOrigin,
			height: "min(680px, calc(100vh - 32px))",
			onCloseButton: () => {
				this.cancelHostedOrder("modal_close")
				options?.onCancel?.(this.activeOrderId || undefined)
			},
			onMessage: (data, container, event) => {
				const isIframeMessage = event.source === this.iframe?.contentWindow
				if (
					isIframeMessage &&
					(data.type === "PAYMENT_READY" ||
						data.type === "PAYMENT_RESIZE" ||
						data.type === "PAYMENT_STARTED")
				) {
					this.markPaymentIframeReady()
				}
				switch (data.type) {
					case "PAYMENT_READY":
						break
					case "PAYMENT_STARTED":
						if (data.orderId) {
							this.activeOrderId = data.orderId
						}
						break
					case "PAYMENT_SUCCESS":
						this.paymentCompleted = true
						options?.onSuccess?.(data.orderId)
						break
					case "PAYMENT_CANCELLED":
						this.cancelHostedOrder("payment_cancelled", data.orderId)
						options?.onCancel?.(data.orderId)
						break
					case "PAYMENT_RESIZE":
						break
					case "PAYMENT_CLOSE":
						this.cancelHostedOrder("payment_close", data.orderId)
						this.close()
						options?.onClose?.()
						break
				}
			},
			width: "min(450px, 100%)",
		})

		if (hostedFrame) {
			this.showPaymentIframeLoading(hostedFrame.container, options)
			this.startPaymentIframeWatchdog({
				container: hostedFrame.container,
				finalUrl,
				paymentOptions: options,
			})
		}
	}

	close() {
		super.close()
		this.cleanupPaymentFrameState()
	}

	/**
	 * Poll order status from integrator's API endpoint
	 * @param statusUrl - The integrator's API endpoint to check order status
	 * @param options - Polling options
	 * @returns Promise that resolves when order is paid or rejects on timeout/failure
	 */
	async pollOrderStatus(
		statusUrl: string,
		options?: PollOptions,
	): Promise<OrderStatus> {
		const interval = options?.interval || 3000
		const timeout = options?.timeout || 300000
		const startTime = Date.now()

		return new Promise((resolve, reject) => {
			const poll = async () => {
				try {
					const response = await fetch(statusUrl)
					if (!response.ok) {
						throw new Error(`Status check failed: ${response.status}`)
					}

					const status: OrderStatus = await response.json()
					options?.onStatusChange?.(status)

					if (status.status === "PAID") {
						resolve(status)
						return
					}

					if (
						status.status === "CANCELLED" ||
						status.status === "FAILED" ||
						status.status === "EXPIRED"
					) {
						reject(new Error(`Order ${status.status.toLowerCase()}`))
						return
					}

					// Check timeout
					if (Date.now() - startTime > timeout) {
						reject(new Error("Polling timeout"))
						return
					}

					// Continue polling
					setTimeout(poll, interval)
				} catch (error) {
					// On network error, continue polling unless timeout
					if (Date.now() - startTime > timeout) {
						reject(error)
						return
					}
					setTimeout(poll, interval)
				}
			}

			poll()
		})
	}
}

export interface WechatMessageBindingUIOptions {
	bindingUrl: string
	displayMode?: "redirect" | "popup"
	popupName?: string
}

export class MessageUI {
	openWechatBinding(options: WechatMessageBindingUIOptions) {
		const bindingUrl = options.bindingUrl?.trim()
		if (!bindingUrl) {
			throw new Error("bindingUrl is required")
		}
		if (options.displayMode === "popup") {
			const popup = window.open(
				bindingUrl,
				options.popupName || "youidian-wechat-message-binding",
				"width=480,height=720,noopener,noreferrer",
			)
			if (!popup) {
				window.location.href = bindingUrl
			}
			return
		}
		window.location.href = bindingUrl
	}
}

export function createMessageUI() {
	return new MessageUI()
}

/**
 * Convenience function to create a PaymentUI instance
 */
export function createPaymentUI(): PaymentUI {
	return new PaymentUI()
}

export type {
	LoginDeviceType,
	LoginEnvironment,
	LoginEnvironmentInput,
} from "./environment"
export { detectLoginEnvironment } from "./environment"
export type {
	LoginCallbackOptions,
	LoginDisplayMode,
	LoginEventData,
	LoginEventType,
	LoginParams,
	LoginResult,
	LoginUIOptions,
} from "./login"
export { createLoginUI, handleLoginCallbackIfPresent, LoginUI } from "./login"
